import React, { useCallback, useEffect, useState, useRef } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import SignaturePad from 'react-signature-pad-wrapper';
import PropTypes from 'prop-types';
import { usePrevious } from '../../../utils/utils';
import { intl } from '../../../providers/IntlProvider';
import style from './SignaturePad.module.less';

// length of 'data:image/png;base64,'
const DATA_URL_PREFIX_LENGTH = 22;

const Signature = ({ setAcceptanceSig, width, revertChanges, signature, description }) => {
  const [canvasTouched, setCanvasTouched] = useState(false);
  const sigPadRef = useRef(null);
  const previousSignature = usePrevious(signature);

  const clear = useCallback(() => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
      setCanvasTouched(false);
      setAcceptanceSig('', '', '');
    }
  }, [setAcceptanceSig]);

  const onSignatureBegin = useCallback(() => {
    if (!canvasTouched) {
      setCanvasTouched(true);
    }
  }, [canvasTouched]);

  const onSignatureEnd = useCallback(() => {
    if (sigPadRef.current) {
      const signatureDataSvg = sigPadRef.current.toSVG();
      const signatureData = sigPadRef.current.toDataURL('image/png').substring(DATA_URL_PREFIX_LENGTH);
      const signedAt = new Date().toISOString();
      setAcceptanceSig(signatureData, signatureDataSvg, signedAt);
    }
  }, [setAcceptanceSig]);

  useEffect(() => {
    const signaturePad = sigPadRef.current?.signaturePad;

    if (signaturePad) {
      signaturePad.addEventListener('beginStroke', onSignatureBegin);
      signaturePad.addEventListener('endStroke', onSignatureEnd);

      return () => {
        signaturePad.removeEventListener('beginStroke', onSignatureBegin);
        signaturePad.removeEventListener('endStroke', onSignatureEnd);
      };
    }
    return undefined;
  }, [onSignatureBegin, onSignatureEnd]);

  useEffect(() => {
    if (previousSignature && signature === '') clear();
  }, [clear, previousSignature, signature]);

  useEffect(() => {
    if (revertChanges) clear();
  }, [clear, revertChanges]);

  return (
    <div>
      <div className={style.signaturePad}>
        {/* Canvas height and width should match the pad so the signature doesn't disappear when resizing */}
        <SignaturePad
          ref={sigPadRef}
          width={width}
          height={100}
          canvasProps={{
            style: {
              width,
              height: 100,
            },
          }}
        />
        <div className={style.signatureHelper} data-test="signatureHelper">
          <span>{description || intl.formatMessage({ id: 'text.pleaseSignHere' })}</span>
          {canvasTouched && (
            <button type="button" onClick={clear} className={style.clearButton}>
              {intl.formatMessage({ id: 'form.button.clear' })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

Signature.propTypes = {
  setAcceptanceSig: PropTypes.func.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  revertChanges: PropTypes.bool,
  signature: PropTypes.string,
  description: PropTypes.string,
};

Signature.defaultProps = {
  width: 500,
  signature: '',
  revertChanges: false,
  description: '',
};

export default Signature;
